function toNumber(value) {
  const parsed = Number(
    String(value ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function normalizeAmount(value) {
  return Number(toNumber(value).toFixed(2));
}

function parseDateValue(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const dt = new Date(`${value}T00:00:00`);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [dd, mm, yyyy] = value.split("/");
      const dt = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  if (typeof value === "object" && value?.seconds) {
    const dt = new Date(value.seconds * 1000);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  return null;
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(key) {
  const [year, month] = key.split("-").map(Number);
  const dt = new Date(year, month - 1, 1);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

function getPaymentEntryAmount(entry) {
  const directAmount = toNumber(entry?.totalAmount ?? entry?.amount);
  if (directAmount > 0) return directAmount;

  return (
    toNumber(entry?.feePaymentAmount) +
    toNumber(entry?.transportPaymentAmount) +
    toNumber(entry?.arrearPaymentAmount) +
    toNumber(entry?.miscPaymentAmount)
  );
}

function getRecordFallbackCollectedTotal(record) {
  const feePaid = toNumber(
    record?.feePaidAmount ?? record?.paidAmount ?? record?.paymentAmount,
  );
  const transportPaid = toNumber(
    record?.transportPaidAmount ?? record?.transportAmount,
  );
  const arrearPaid = toNumber(record?.arrearPaidAmount);
  const miscPaid = Array.isArray(record?.miscellaneousPayments)
    ? record.miscellaneousPayments.reduce(
        (sum, item) => sum + toNumber(item?.amount),
        0,
      )
    : 0;

  return feePaid + transportPaid + arrearPaid + miscPaid;
}

function getRecordPaymentBreakdown(record) {
  const history = Array.isArray(record?.paymentHistory) ? record.paymentHistory : [];

  if (history.length > 0) {
    const entries = [];
    let total = 0;

    history.forEach((entry) => {
      const amount = getPaymentEntryAmount(entry);
      if (amount <= 0) return;

      const paymentDt = parseDateValue(entry?.paymentDate || entry?.recordedAt);
      total += amount;

      if (paymentDt) {
        entries.push({
          date: paymentDt,
          amount,
        });
      }
    });

    return {
      total: normalizeAmount(total),
      entries,
    };
  }

  const fallbackTotal = getRecordFallbackCollectedTotal(record);
  const fallbackDate = parseDateValue(
    record?.paymentDate || record?.updatedAt || record?.createdAt,
  );

  return {
    total: normalizeAmount(fallbackTotal),
    entries:
      fallbackDate && fallbackTotal > 0
        ? [{ date: fallbackDate, amount: fallbackTotal }]
        : [],
  };
}

function getRecordOutstanding(record) {
  if (hasValue(record?.totalOutstanding)) {
    return Math.max(0, toNumber(record.totalOutstanding));
  }

  const balanceFromFields =
    toNumber(record?.balanceFee) +
    toNumber(record?.balanceTransportFees) +
    toNumber(record?.balanceArrear);

  if (Math.abs(balanceFromFields) > 0.01) {
    return Math.max(0, balanceFromFields);
  }

  const totalFees = toNumber(record?.totalOfAllFees ?? record?.totalAnnualFees);
  const previousDue = toNumber(record?.previousYearArrears ?? record?.previousDue);
  const lateFee = toNumber(record?.lateFee);
  const discount =
    toNumber(record?.discountAmount) + toNumber(record?.scholarshipAmount);
  const paid =
    toNumber(record?.feePaidAmount ?? record?.paidAmount ?? record?.paymentAmount) +
    toNumber(record?.transportPaidAmount ?? record?.transportAmount) +
    toNumber(record?.arrearPaidAmount);

  return Math.max(0, totalFees + previousDue + lateFee - discount - paid);
}

function getRecordFeeDemand(record) {
  const directTotal = toNumber(record?.totalOfAllFees ?? record?.totalAnnualFees);
  const previousYearArrears = toNumber(
    record?.previousYearArrears ?? record?.previousDue,
  );

  if (directTotal > 0 || previousYearArrears > 0) {
    return directTotal + previousYearArrears;
  }

  const monthlyPerMonth =
    toNumber(record?.monthlyTuitionFee) +
    toNumber(record?.monthlyMaintenanceFee) +
    toNumber(record?.monthlySportsFee) +
    toNumber(record?.monthlyComputerFee);
  const monthlyAnnual = monthlyPerMonth * (toNumber(record?.monthlyFeeMonths) || 12);
  const otherCharges =
    toNumber(record?.otherAnnualCharge) +
    toNumber(record?.otherIdCharge) +
    toNumber(record?.otherDiaryCharge) +
    toNumber(record?.otherExamFee);
  const transportAnnual =
    toNumber(record?.transportFeePerMonth) * (toNumber(record?.transportMonths) || 11);

  return monthlyAnnual + otherCharges + transportAnnual + previousYearArrears;
}

export function formatInr(value, maximumFractionDigits = 0) {
  return `INR ${toNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits,
  })}`;
}

export function createEmptyAccountsAnalytics(now = new Date()) {
  const key = monthKey(now);
  const [year, month] = key.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();

  return {
    totalStudents: 0,
    pendingStudents: 0,
    totalOutstanding: 0,
    totalFeeDemand: 0,
    totalCollectedAllTime: 0,
    currentMonthCollection: 0,
    collectionRatio: 0,
    currentMonthKey: key,
    currentMonthLabel: formatMonthLabel(key),
    monthOptions: [{ key, label: formatMonthLabel(key) }],
    monthlySeries: [{ monthKey: key, label: formatMonthLabel(key), collection: 0 }],
    dailySeriesByMonth: {
      [key]: Array.from({ length: daysInMonth }, (_, idx) => ({
        day: String(idx + 1),
        collection: 0,
      })),
    },
  };
}

export function buildAccountsAnalytics(records, now = new Date()) {
  const base = createEmptyAccountsAnalytics(now);
  const nowKey = base.currentMonthKey;

  const monthlyCollectionMap = new Map();
  const dailyCollectionMaps = new Map();

  let totalOutstanding = 0;
  let totalFeeDemand = 0;
  let totalCollectedAllTime = 0;
  let pendingStudents = 0;

  records.forEach((record) => {
    const outstanding = getRecordOutstanding(record);
    totalOutstanding += outstanding;
    if (outstanding > 0.01) {
      pendingStudents += 1;
    }

    totalFeeDemand += getRecordFeeDemand(record);

    const paymentInfo = getRecordPaymentBreakdown(record);
    totalCollectedAllTime += paymentInfo.total;

    paymentInfo.entries.forEach(({ date, amount }) => {
      if (!date || amount <= 0) return;

      const key = monthKey(date);
      monthlyCollectionMap.set(key, (monthlyCollectionMap.get(key) || 0) + amount);

      if (!dailyCollectionMaps.has(key)) {
        dailyCollectionMaps.set(key, new Map());
      }

      const day = date.getDate();
      const dayMap = dailyCollectionMaps.get(key);
      dayMap.set(day, (dayMap.get(day) || 0) + amount);
    });
  });

  if (!monthlyCollectionMap.has(nowKey)) {
    monthlyCollectionMap.set(nowKey, 0);
  }

  const monthKeys = Array.from(monthlyCollectionMap.keys()).sort();
  const monthOptions = monthKeys.map((key) => ({
    key,
    label: formatMonthLabel(key),
  }));

  const monthlySeries = monthKeys.map((key) => ({
    monthKey: key,
    label: formatMonthLabel(key),
    collection: normalizeAmount(monthlyCollectionMap.get(key) || 0),
  }));

  const dailySeriesByMonth = {};
  monthKeys.forEach((key) => {
    const [year, month] = key.split("-").map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const dayMap = dailyCollectionMaps.get(key) || new Map();

    dailySeriesByMonth[key] = Array.from({ length: daysInMonth }, (_, idx) => {
      const day = idx + 1;
      return {
        day: String(day),
        collection: normalizeAmount(dayMap.get(day) || 0),
      };
    });
  });

  const currentMonthCollection = normalizeAmount(monthlyCollectionMap.get(nowKey) || 0);
  const collectionRatio =
    totalFeeDemand > 0 ? (totalCollectedAllTime / totalFeeDemand) * 100 : 0;

  return {
    totalStudents: records.length,
    pendingStudents,
    totalOutstanding: normalizeAmount(totalOutstanding),
    totalFeeDemand: normalizeAmount(totalFeeDemand),
    totalCollectedAllTime: normalizeAmount(totalCollectedAllTime),
    currentMonthCollection,
    collectionRatio: Number(collectionRatio.toFixed(2)),
    currentMonthKey: nowKey,
    currentMonthLabel: formatMonthLabel(nowKey),
    monthOptions,
    monthlySeries,
    dailySeriesByMonth,
  };
}
